/**
 * Admin Affiliates API Service
 * Handles affiliate program and voucher management
 */

import { get, post, put, del, API_BASE_URL } from "../client";
import { Voucher } from "../../../types";

// === Response Types ===

export interface AffiliateStats {
  totalMitra: number;
  totalVoucherUsed: number;
  totalCommissionPaid: number;
  omsetAffiliate: number;
  activeVouchers: number;
}

interface ApiVoucher {
  id: string;
  partnerId: string;
  code: string;
  discountValue: number;
  discountType: "percent" | "fixed";
  partnerCommission: number;
  isActive: number;
  createdAt: string;
  updatedAt: string;
  partner?: {
    id: string;
    userId: string;
    user?: {
      fullName: string;
      id: string;
    };
  } | null;
}

interface AffiliateResponse {
  message: string;
  data: {
    totalMitra: number;
    totalVoucherUsed: number;
    totalCommissionPaid: number;
    omsetAffiliate: number;
    activeVouchers: number;
    bookingHistory: any;
  };
}

export interface PaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  firstPage: number;
  firstPageUrl?: string;
  lastPageUrl?: string;
  nextPageUrl?: string | null;
  previousPageUrl?: string | null;
}

interface VouchersResponse {
  success: boolean;
  message: string;
  data: {
    meta: PaginationMeta;
    data: ApiVoucher[];
  };
}

export interface PaginatedVouchers {
  data: Voucher[];
  meta: PaginationMeta;
}

interface VoucherResponse {
  success: boolean;
  message: string;
  data: ApiVoucher;
}

// === Request Types ===

export interface CreateVoucherRequest {
  partnerId: string;
  code: string;
  discountValue: number;
  discountType: "percent" | "fixed";
  partnerCommission: number;
  isActive?: boolean;
}

// === Transform Functions ===

function transformVoucher(apiVoucher: any): Voucher {
  const partnerName =
    apiVoucher.partner?.user?.fullName ||
    apiVoucher.partner?.user?.full_name ||
    apiVoucher.partner_name ||
    apiVoucher.partnerName ||
    "Unknown";

  return {
    id: apiVoucher.id,
    code: apiVoucher.code,
    type: apiVoucher.discountType ?? apiVoucher.discount_type ?? "percent",
    value: apiVoucher.discountValue ?? apiVoucher.discount_value ?? 0,
    creatorId: apiVoucher.partnerId || apiVoucher.partner?.id || "",
    creatorName: partnerName,
    partnerCommission:
      apiVoucher.partnerCommission ?? apiVoucher.partner_commission ?? 6,
  };
}

// === API Functions ===

/**
 * Get affiliate program statistics
 */
export async function getAffiliateStats(): Promise<AffiliateStats> {
  const response = await get<AffiliateResponse>("/admin/affiliates");
  return {
    totalMitra: response.data.totalMitra,
    totalVoucherUsed: response.data.totalVoucherUsed,
    totalCommissionPaid: response.data.totalCommissionPaid,
    omsetAffiliate: response.data.omsetAffiliate,
    activeVouchers: response.data.activeVouchers,
  };
}

/**
 * Get all vouchers with pagination and search
 */
export async function getVouchers(
  page = 1,
  search = "",
  perPage = 5
): Promise<PaginatedVouchers> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("perPage", String(perPage));
  if (search) {
    params.append("search", search);
  }
  const response = await get<VouchersResponse>(
    `/admin/vouchers?${params.toString()}`
  );

  const payload = (response as any).data || {};
  const rows = Array.isArray(payload.data) ? payload.data : [];
  const meta = payload.meta || {};
  const total = Number(meta.total || rows.length || 0);
  const parsedPerPage = Number(meta.perPage || perPage || 5);
  const currentPage = Number(meta.currentPage || page || 1);
  const lastPage = Number(
    meta.lastPage || Math.max(1, Math.ceil(total / Math.max(parsedPerPage, 1)))
  );

  return {
    data: rows.map(transformVoucher),
    meta: {
      total,
      perPage: parsedPerPage,
      currentPage,
      lastPage,
      firstPage: 1,
      firstPageUrl: undefined,
      lastPageUrl: "",
      nextPageUrl: currentPage < lastPage ? "" : null,
      previousPageUrl: currentPage > 1 ? "" : null,
    },
  };
}

/**
 * Create a new voucher
 */
export async function createVoucher(
  data: CreateVoucherRequest
): Promise<Voucher> {
  const response = await post<VoucherResponse>("/admin/vouchers", data);
  return transformVoucher(response.data);
}

/**
 * Update a voucher
 */
export async function updateVoucher(
  id: string,
  data: CreateVoucherRequest
): Promise<Voucher> {
  const response = await put<VoucherResponse>(`/admin/vouchers/${id}`, data);
  return transformVoucher(response.data);
}

/**
 * Delete a voucher
 */
export async function deleteVoucher(id: string): Promise<void> {
  await del(`/admin/vouchers/${id}`);
}

// === Import Functions ===

export interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

/**
 * Download voucher import template
 */
export function getVoucherTemplateUrl(): string {
  return `${API_BASE_URL}/admin/imports/vouchers/template`;
}

/**
 * Import vouchers from Excel file
 */
export async function importVouchers(file: File): Promise<ImportResult> {
  const formData = new FormData();
  formData.append("file", file);

  const token = localStorage.getItem("auth_token");
  const response = await fetch(`${API_BASE_URL}/admin/imports/vouchers`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  }
  );

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || "Failed to import vouchers");
  }
  return result.data;
}

/**
 * Bulk delete vouchers
 */
export async function bulkDeleteVouchers(ids: string[]): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/admin/vouchers/bulk`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
    },
    body: JSON.stringify({ ids }),
  });

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || "Failed to bulk delete vouchers");
  }
  return result;
}
